import React, { useEffect, useRef, useCallback, useState } from 'react';
import Auth, {
    ClientID,
    RedirectURI,
    ErrorbackURI,
    AuthURL,
    ParamsSerializer,
    User,
} from '../../data/auth-service';
import { useNavigate } from 'react-router-dom';
import { get as _get } from 'lodash';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        '& iframe': {
            border: 0,
            width: '500px',
            height: '250px',
        },
    },
    loading: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));

const BRCLoginWidget = ({ onError, onSuccess }) => {
    const navigate = useNavigate();
    const called = useRef(false);
    const classes = useStyles();

    const [params, setParams] = useState(null);
    const [state] = useState(Auth.maybeNewState());
    const [code, setCode] = useState(null);
    const [kick, setKick] = useState(0);
    const [loaded, setLoaded] = useState(0);

    // login is a three step process
    // LOGIN STEP 1: navigate to the auth server with a code challenge
    useEffect(() => {
        setLoaded(0);
        setParams(null);
        Auth.maybeNewCodeChallenge().then(codeChallenge => {
            const params = ParamsSerializer({
                client_id: ClientID,
                response_type: 'code',
                code_challenge: codeChallenge,
                code_challenge_method: 'S256',
                scope: 'all',
                state,
                cmc_home: RedirectURI,
                // redirect_uri: RedirectURI,
                errorback_uri: ErrorbackURI,
            });
            setParams(params);
        });
    }, [state, kick]);

    // LOGIN STEP 2: exchange the code for a token
    // LOGIN STEP 3: (in auth-service.js) call -self to get the user profile
    const loginCallback = useCallback(({ code, state }) => {
        Auth.getTokens({ code, state, RedirectURI }).then(res => {
            onSuccess(res);
        }, () => {
            navigate('/oauth/errorback');
        });
    }, [onSuccess, navigate]);

    // prevent rerender caused by StrictMode
    useEffect(() => {
        if (!code || !state) return;
        if (called.current || !RedirectURI) return; // prevent rerender caused by StrictMode
        called.current = true;

        loginCallback({ code, state });
    }, [loginCallback, code, state]);

    useEffect(() => {
        let expectedOrigin = window.location.origin;
        try {
            expectedOrigin = new URL(AuthURL, window.location.origin).origin;
        } catch (err) {
            // Fallback to current origin if AuthURL is malformed.
        }

        // we don't need two listeners, one on the parent and one on the iframe
        const messageHandler = (e) => {
            if (_get(e, 'data.type') !== 'oOauth') return;
            if (_get(e, 'origin') !== expectedOrigin) return;

            if (_get(e, 'data.error')) {
                // kick the login widget
                setKick(current => current + 1);
                setLoaded(0);
                onError(e.data.error);
                return;
            }

            // this is not ours, it belongs to the parent window. pop out.
            if (_get(e, 'data.state') !== state) {
                return;
            }

            setCode(e.data.code);
        };

        window.addEventListener('message', messageHandler);
        return () => {
            window.removeEventListener('message', messageHandler);
        };
    }, [onError, state]);

    // don't linger unnecessarily on the login page
    useEffect(() => {
        const auth = Auth.getAuth();

        if (auth && auth.user) {
            onSuccess(auth);
            return;
        }

        // if we have a refresh token, let's try to use that
        if (auth && auth.refresh_token) {
            Auth.refresh().then(() => {
                const user = User();
                if (user) {
                    onSuccess({ user });
                    return;
                }
                Auth.clearSession();
            }, () => {
                // if we fail to refresh, let the user login
                Auth.clearSession();
            });
        }

        // maybe kick the login widget, but give the error callback a chance to bubble up first
        if (loaded > 1) {
            setTimeout(() => {
                if (loaded > 1) {
                    setKick(current => current + 1);
                }
            }, 1111); // you know you failed when you have to use timeouts
        }
    }, [onSuccess, loaded]);

    return (
        <div className={classes.root}>
            {!loaded && <div className={classes.loading}>
                <CircularProgress/>
            </div>}
            {params &&
                <iframe
                    scrolling="no"
                    src={`${AuthURL}/oauth/authorize?${params}`}
                    style={{ Height: '50vh', Width: 600 }}
                    onLoad={() => setLoaded(current => current + 1)}
                >
                </iframe>
            }
        </div>
    );
};
BRCLoginWidget.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};

const BRCLogin = ({ onSuccess, onError }) => {
    // const [disableSubmit, setDisableSubmit] = useState(true);
    const navigate = useNavigate();

    const login = (e) => {
        e.preventDefault();
    };

    const forgotPassword = () => {
        navigate('/signin/forgot');
    };

    return (
        <Paper elevation={3}>
            <Box p={5}>
                <form noValidate autoComplete="off" onSubmit={login}>
                    <BRCLoginWidget onError={ onError } onSuccess={ onSuccess } />
                    <Box mt={2}>
                        <Button className="mt-25 ml-10" color="primary" ml={5} aria-label="Forgot password?"
                            onClick={forgotPassword}>Forgot Password?</Button>
                    </Box>
                </form>
            </Box>
        </Paper>
    );
};
BRCLogin.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};

const BRCLoginErrorback = () => {
    useEffect(() => {
        window.parent.postMessage({ type: 'oOauth', error: 'Login error' }, window.location.origin);
    }, []);
    return null;
};

const BRCLoginCallback = () => {
    const called = useRef(false);
    useEffect(() => {
        if (Auth.loggedIn()) return;
        if (called.current) return; // prevent rerender caused by StrictMode
        called.current = true;

        (async () => {
            const code = new URLSearchParams(window.location.search).get('code');
            const state = new URLSearchParams(window.location.search).get('state');

            // bubble back up to parent to handle the callback
            if (window.parent !== window) {
                window.parent.postMessage({ type: 'oOauth', code, state }, window.location.origin);
            }
        })();
    }, []);
    return null;
};

export {
    BRCLogin,
    BRCLoginCallback,
    BRCLoginErrorback,
};
